// src/app/components/cart/ShareCartDialog.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Autocomplete,
  Chip,
  Button,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { CartResponse, ShareShoppingCartRequest, ShoppingCartUser } from "../../models/cart/types";
import dataService from "../../services/dataService";

type ShareCartDialogProps = {
  open: boolean;
  onClose: () => void;
  cart: CartResponse | null;
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ShareCartDialog: React.FC<ShareCartDialogProps> = ({ open, onClose, cart }) => {
  const [options, setOptions] = useState<ShoppingCartUser[]>([]);
  const [recipients, setRecipients] = useState<ShoppingCartUser[]>([]);
  const [subject, setSubject] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [sending, setSending] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; ok: boolean; text: string }>({
    open: false,
    ok: true,
    text: "",
  });

  // initialize options and defaults when dialog opens
  useEffect(() => {
    if (!open) return;

    const clean = (cart?.shareEmails ?? [])
      .map(u => ({
        id: Number(u?.id ?? 0),
        email: (u?.email ?? "").trim(),
      }))
      .filter(u => u.id > 0 && u.email && emailRegex.test(u.email));

    const seen = new Set<number>();
    const unique = clean.filter(u => {
      if (seen.has(u.id)) return false;
      seen.add(u.id);
      return true;
    });

    setOptions(unique);
    setRecipients([]);
    setSubject(`${cart?.customerName} LansingNOW shared a shopping cart with you`);
    setMessage("");
  }, [open, cart?.shareEmails, cart?.customerName]);

  const invalidEmails = recipients.filter((r) => !emailRegex.test(r.email));
  const canSend = recipients.length > 0 && invalidEmails.length === 0 && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const payload: ShareShoppingCartRequest = {
        recipients: recipients.map(x => x.id),
        subject: subject.trim(),
        message: message.trim(),
      };

      const res = await dataService.shareShoppingCart(payload);
      if (!res?.success) throw new Error(res?.message || "Failed to send email.");

      setSnack({ open: true, ok: true, text: res.message || "Email sent successfully." });
      onClose();
    } catch (err: any) {
      setSnack({ open: true, ok: false, text: err?.message || "Failed to send email." });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "visible",
            fontFamily: "Montserrat, Verdana, sans-serif",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Montserrat, Verdana, sans-serif",
            fontWeight: 700,
            color: "#003B5C",
            pr: 6,
          }}
        >
          Email Cart
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 4 }}>
          <Box className="share-content" sx={{ display: "grid", gap: 2 }}>
            <Autocomplete
              multiple
              options={options}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.email)}
              value={recipients}
              onChange={(_, newValue) => setRecipients(newValue as ShoppingCartUser[])}
              filterSelectedOptions
              slotProps={{ popper: { sx: { zIndex: 2100 } } }}
              renderTags={(value, getTagProps) =>
                value.map((o, i) => (
                  <Chip
                    label={typeof o === "string" ? o : o.email}
                    variant="outlined"
                    {...getTagProps({ index: i })}
                    key={typeof o === "string" ? o : o.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Recipients"
                  placeholder="Type an email and press Enter"
                  size="small"
                />
              )}
            />

            <TextField
              label="Subject"
              value={subject}
              size="small"
              InputProps={{ readOnly: true }}
            />

            <TextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={5}
              inputProps={{ style: { maxHeight: 130 } }}
            />
          </Box>

          {!!invalidEmails.length && (
            <Typography sx={{ mt: 1 }} color="error" variant="body2">
              Invalid email(s): {invalidEmails.map(x => x.email).join(", ")}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="text" sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            id="send-email-btn"
            onClick={handleSend}
            variant="contained"
            className="checkout-button"
            disabled={!canSend}
            sx={{ textTransform: "none", fontWeight: 800, height: 40 }}
          >
            {sending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.ok ? "success" : "error"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareCartDialog;